import { CHANNEL_TYPE, FUNNEL_STATUS, FUNNEL_TYPE } from 'src/app/enum/constants';
import { ILastMessage } from 'src/app/interfaces';
import { AddressInput, IAddress, ICardEmail, ICardPhone, ICardV2 } from 'src/app/interfaces/card';
import type { TimelineEntityInfo } from '../../interfaces/timeline.interface';

export type BoardColumnType = 'DEFAULT' | 'SUCCESS' | 'FAILED';
export type LeadConversionTargetType = 'deal';

export interface BoardColumnColor {
  text: string;
  background: string;
}

export interface CreateBoardColumnPayload {
  funnel_id: string;
  name: string;
  color: BoardColumnColor;
  position?: number;
  type?: BoardColumnType;
}

export interface UpdateBoardColumnPayload {
  name?: string;
  color?: Partial<BoardColumnColor>;
  position?: number;
}

export interface ReorderBoardColumnsPayload {
  funnel_id: string;
  columns: { id: string; position: number }[];
}

export interface BoardColumn {
  id: string;
  active: boolean;
  created_by: string | null;
  created_at: string;
  updated_by: string | null;
  updated_at: string | null;
  deleted_by: string | null;
  deleted_at: string | null;
  company_id: string;
  funnel_id: string;
  name: string;
  color: BoardColumnColor;
  position: number;
  type: BoardColumnType;
  count_cards: string;
  profit?: number;
  currency?: string;
}

export interface BoardFunnel {
  id: string;
  name: string;
  type: FUNNEL_TYPE;
  status: FUNNEL_STATUS;
  currency: string;
  view_fields?: string[];
  create_fields?: string[];
}

export interface GetColumnsPayload {
  config: {
    funnel_id: string;
    is_view_success?: boolean;
    is_view_failed?: boolean;
  };
  filters: BoardFilterCondition[];
  filter_mode?: BoardFilterMode;
}

export interface GetFunnelsPayload {
  type: string;
}

export interface GetAllCardsPayload {
  funnel_id: string;
  filters?: BoardFilterCondition[];
  filter_mode?: BoardFilterMode;
  limit: number;
}

export interface GetCardsPayload {
  column_id: string;
  limit: number;
  last_id?: string;
  filters?: BoardFilterCondition[];
  filter_mode?: BoardFilterMode;
}

export interface BoardField {
  id: string;
  name: string;
  identifier: string;
  input_type: string;
  position: number;
  icon?: string;
  data_type?: string;
  options?: string[];
  default_value?: unknown;
  is_required?: boolean;
  is_hidden?: boolean;
  is_readonly?: boolean;
  is_system?: boolean;
}

export type BoardFilterMode = 'AND' | 'OR';
export type BoardFilterOperator =
  | 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte'
  | 'ilike' | 'not_ilike' | 'starts_with' | 'ends_with'
  | 'in' | 'not_in' | 'between' | 'is_null' | 'is_not_null'
  | 'is' | 'like' | 'not_like';
export type BoardFilterFieldType =
  | 'text' | 'textarea' | 'phone' | 'email' | 'link' | 'select' | 'uuid'
  | 'number' | 'price' | 'date' | 'datetime' | 'boolean' | 'checkbox' | 'manager';

export interface BoardFilterCondition {
  key: string;
  field_key?: string;
  type: BoardFilterOperator;
  operator?: BoardFilterOperator;
  value: unknown;
  is_custom: boolean;
  field_type?: BoardFilterFieldType;
}

export interface BoardFilterViewSettings {
  is_view_success: boolean;
  is_view_failed: boolean;
}

export interface BoardFilterSetData {
  version: 1;
  mode: BoardFilterMode;
  conditions: BoardFilterCondition[];
  view: BoardFilterViewSettings;
}

export interface BoardFilterSet {
  id: string;
  company_id: string;
  user_id: string;
  funnel_id?: string;
  name: string;
  type: string;
  is_shared: boolean;
  x_data: BoardFilterSetData;
}

export interface SaveBoardFilterPayload {
  name: string;
  is_shared?: boolean;
  x_data: BoardFilterSetData;
  type?: string;
}

export interface SaveFunnelFilterPayload extends SaveBoardFilterPayload {
  funnel_id: string;
  type: string;
}

export interface BoardFieldsResponse {
  default_fields: BoardField[];
  custom_fields: BoardField[];
  create_fields: string[];
  create_default_fields: BoardField[];
  view_fields: BoardField[];
}

export interface CardCreatedEvent {
  card: BoardCard;
}

export interface CardUpdatedEvent {
  card: BoardCard;
}

export interface CardDetailUpdatedEvent {
  card: ICardV2;
}

export interface CardTimelineSocketEvent {
  timelines: CardTimelineEvent[];
  entity_id: string;
  entity_type: string;
}

export interface CardDeletedEvent {
  card_id: string;
  funnel_id?: string;
  column_id?: string;
}

export interface BoardColumnsUpdatedEvent {
  funnel_id: string;
}

export interface BoardFieldsUpdatedEvent {
  fields: BoardField[];
  funnel?: BoardFunnel | null;
  funnel_id: string;
  user_id?: string;
  author_name?: string;
}

export interface FunnelCreatedEvent {
  funnel: BoardFunnel;
  funnel_id: string;
  type: FUNNEL_TYPE;
}

export interface FunnelUpdatedEvent {
  funnel: BoardFunnel;
  funnel_id: string;
  type: FUNNEL_TYPE;
}

export interface FunnelDeletedEvent {
  funnel?: BoardFunnel;
  funnel_id: string;
  type: FUNNEL_TYPE;
}

export interface CardViewer {
  id: string;
  first_name: string;
  last_name: string;
  avatar_url: string | null;
  email: string;
}

export interface CardPresenceEvent {
  card_id: string;
  company_id?: string;
  funnel_id?: string;
  viewers: CardViewer[];
}

export interface FunnelPresenceEvent {
  company_id?: string;
  funnel_id: string;
  viewers: CardViewer[];
}

export type ImportProgressStatus = 'downloading' | 'parsing' | 'processing' | 'completed' | 'failed';

export interface ImportProgressEvent {
  funnel_id?: string;
  status: ImportProgressStatus;
  message?: string;
  progress?: number;
  current?: number;
  total?: number;
  result?: { total_records: number, imported: number, failed: number };
  error?: { message?: string };
}

export type ExportFormat = 'CSV' | 'JSON' | 'TOML';

export type ExportProgressStatus = 'processing' | 'completed' | 'failed';

export interface ExportProgressEvent {
  funnel_id?: string;
  status: ExportProgressStatus;
  progress?: number;
  error?: { message?: string };
}

export interface LeadConversionResultEvent {
  status: 'completed' | 'failed';
  user_id: string;
  lead_card_id: string;
  lead_funnel_id: string;
  trigger_column_id: string;
  target_type: LeadConversionTargetType;
  deal_funnel_id?: string | null;
  deal_card_id?: string | null;
  batch_id?: string | null;
  reverted?: boolean;
  error?: { message?: string };
}

export interface LeadConversionRequest {
  target_type: LeadConversionTargetType;
  deal_funnel_id: string;
  batch_id?: string | null;
}

export interface CreateFunnelDialogSave {
  name: string;
}

export interface UpdateFunnelPayload {
  name?: string;
  view_fields?: string[];
  create_fields?: string[];
  [k: string]: unknown;
}

export interface CreateFunnelPayload {
  name: string;
  type: string;
  status: FUNNEL_STATUS;
  create_fields?: string[];
  view_fields?: string[];
}

export interface MoveCardPayload {
  column_id: string;
  top_card_id: string | null;
  bottom_card_id: string | null;
  old_column_id?: string;
  funnel_id?: string;
  lead_conversion?: LeadConversionRequest;
}

export interface BoardMassActionCardResult {
  card_id: string;
  status: 'completed' | 'failed';
  deal_card_id?: string | null;
  error?: { message?: string };
}

export interface BoardMassActionResponse {
  affected: number;
  moved_count?: number;
  failed_count?: number;
  reverted_count?: number;
  queued_count?: number;
  lead_conversion_batch_id?: string | null;
  results?: BoardMassActionCardResult[];
}

export type BoardMassActionPayload =
  | { action: 'DELETE'; ids: string[]; column_id?: string }
  | {
      action: 'MOVE';
      ids: string[];
      payload?: {
        column_id?: string;
        lead_conversion?: LeadConversionRequest;
      };
    };

export interface MergeCardsPayload {
  /** All cards taking part in the merge, including the target one. */
  ids: string[];
  /** The card that stays after merging. */
  target_id: string;
  /** Final field values for the target card, custom ones nested in `custom_fields`. */
  fields: Record<string, unknown>;
}

export interface CreateCardPayload {
  funnel_id: string;
  column_id: string;
  name: string;
  price?: number;
  currency?: string;
  assigned_id?: string | null;
  phones?: string[];
  emails?: string[];
  addresses?: AddressInput[];
  custom_fields?: Record<string, unknown>;
  /** `{ id }` links an existing contact, an item without `id` creates a new one */
  contacts?: CardContactInput[];
  /** `{ id }` links an existing company, without `id` a new one is created */
  company?: CardCompanyInput;
}

export interface FieldType {
  name: string;
  input_type: string;
  data_type: string;
  icon: string;
}

export interface GetManagersParams {
  take?: number;
  page?: number;
}

export interface DynamicBoardCard extends BoardCard {
  created_by_user: BoardManager | null;
  updated_by_user: BoardManager | null;
  assigned_id_user: BoardManager | null;
  viewers?: CardViewer[];
}

export interface BoardCardMoveRequest {
  card: DynamicBoardCard;
  columnId: string;
}

export interface BoardManager {
  id: string;
  first_name: string;
  last_name: string;
  avatar_url: string;
  email: string;
  phone: string;
  referral_code: string;
  status: string;
  role: string;
  is_admin: boolean;
  active: boolean;
  created_by: string;
  created_at: string;
  updated_by: string;
  updated_at: string;
}

export interface BoardCardColor {
  text: string;
  background: string;
}

export interface BoardCardChat {
  id: string;
  title: string | null;
  channel_name: string | null;
  channel_type: CHANNEL_TYPE | null;
  unread_count?: number | null;
  is_mark_unread?: boolean | null;
  last_message: ILastMessage;
  updated_at: string;
}

export interface ChatMessageNewEvent {
  chat?: {
    id?: string;
    unread_count?: number | null;
    is_mark_unread?: boolean | null;
  };
}

export interface BoardCard {
  id: string;
  parent_id?: string | null;
  active: boolean;
  created_by: string | null;
  created_at: string;
  updated_by: string | null;
  updated_at: string | null;
  deleted_by: string | null;
  deleted_at: string | null;
  company_id: string;
  funnel_id: string;
  column_id: string;
  name: string;
  description: string | null;
  color: BoardCardColor;
  position: number;
  price: number;
  currency: string;
  assigned_id: string | null;
  phones: ICardPhone[] | null;
  emails: ICardEmail[] | null;
  addresses?: IAddress[] | null;
  chats?: BoardCardChat[];
  contacts?: BoardContact[];
  /** A card has at most one company */
  company?: BoardCompany | null;
  custom_fields: Record<string, unknown>;
  rn: string;
}

export enum CardTimelineEventType {
  SYSTEM = 'SYSTEM',
  COMMENT = 'COMMENT',
  CARD = 'CARD',
  REMINDER = 'REMINDER',
  CALL = 'CALL',
  CONTACT = 'CONTACT',
  COMPANY = 'COMPANY',
  TASK = 'TASK',
  CHAT = 'CHAT',
}

export enum CardTimelineEventAction {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
  FIRED = 'FIRED',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

export enum CardTimelineEventSubType {
  STAGE_CHANGE = 'STAGE_CHANGE',
  ASSIGN_MANAGER = 'ASSIGN_MANAGER',
  UNASSIGN_MANAGER = 'UNASSIGN_MANAGER',
  PRICE_CHANGE = 'PRICE_CHANGE',
  CURRENCY_CHANGE = 'CURRENCY_CHANGE',
  CUSTOM_FIELD_CHANGE = 'CUSTOM_FIELD_CHANGE',
  PHONE_CHANGE = 'PHONE_CHANGE',
  EMAIL_CHANGE = 'EMAIL_CHANGE',
  MERGE = 'MERGE',
}

export interface CardTimelineAuthor {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  avatar_url?: string | null;
}

export enum CARD_REMINDER_STATUS {
  PENDING = 'PENDING',
  DONE = 'DONE',
  CANCELLED = 'CANCELLED',
}

export interface CardReminderXData {
  title: string;
  text: string | null;
  due_at: string;
  status: CARD_REMINDER_STATUS;
  reminders: string[];
  assigned_id: string | null;
  reminder_offset: string | null;
  card_reminder_id: string;
}

export interface CardCommentXData {
  comment: string;
}

export interface CardTimelineStage {
  id: string;
  name: string;
  color?: { text?: string; background?: string } | null;
}

export interface CardTimelineStageChangeXData {
  old: CardTimelineStage | null;
  new: CardTimelineStage;
}

export interface CardTimelineManagerChangeXData {
  old: CardTimelineAuthor | null;
  new: CardTimelineAuthor | null;
}

export interface CardTimelinePriceChangeXData {
  old: number | null;
  new: number | null;
}

export interface CardTimelineCurrencyChangeXData {
  old: string | null;
  new: string | null;
}

export interface CardTimelinePhonesChangeXData {
  old?: string[];
  new?: string[];
}

/** Addresses in the timeline come in the request shape, without `id` / `type` / `is_primary` */
export interface CardTimelineAddressesChangeXData {
  old?: AddressInput[];
  new?: AddressInput[];
}

export interface CardTimelineEmailsChangeXData {
  old?: string[];
  new?: string[];
}

export interface CardTimelineFieldDiff {
  old: unknown;
  new: unknown;
}

export interface CardTimelineCardCreateXData {
  name: string;
}

export interface CardTimelineDescriptionChangeXData {
  old: string | null;
  new: string | null;
}

export interface CardTimelineMergedCard {
  id: string;
  name: string;
}

export interface CardTimelineMergeXData {
  target_id: string;
  merged_cards: CardTimelineMergedCard[];
  merged_card_ids: string[];
}

export interface CardTimelineCardUpdateXData {
  name?: CardTimelineDescriptionChangeXData;
  stage?: CardTimelineStageChangeXData;
  assigned?: CardTimelineManagerChangeXData;
  price?: CardTimelinePriceChangeXData;
  currency?: CardTimelineCurrencyChangeXData;
  phones?: CardTimelinePhonesChangeXData;
  emails?: CardTimelineEmailsChangeXData;
  addresses?: CardTimelineAddressesChangeXData;
  description?: CardTimelineDescriptionChangeXData;
  custom_fields?: Record<string, CardTimelineFieldDiff>;
  merge?: CardTimelineMergeXData;
}

export type CardTimelineCardXData = CardTimelineCardCreateXData | CardTimelineCardUpdateXData;

/** `x_data` of the task events: STATUS_CHANGE has `status`, DEADLINE_CHANGE `deadline` (ISO or null) */
export interface CardTimelineTaskUpdateXData {
  status?: { old: string | null; new: string | null };
  deadline?: { old: string | null; new: string | null };
}

/** `x_data` of the chat events (`GET /timeline?entity_type=chat`): TITLE_CHANGE has `title`, MANAGER_CHANGE `manager` */
export interface CardTimelineChatUpdateXData {
  title?: CardTimelineDescriptionChangeXData;
  /** `old` is null for the first assignment, `new` when the manager is removed */
  manager?: CardTimelineManagerChangeXData;
}

export interface CardTimelineLinkedContact {
  id: string;
  first_name: string;
  last_name: string | null;
  middle_name: string | null;
}

export interface CardTimelineContactUpdateXData {
  /** Contact linked to (`new`) or unlinked from (`old`) a card */
  contact?: {
    old: CardTimelineLinkedContact | null;
    new: CardTimelineLinkedContact | null;
  };
  /** Card linked to (`new`) or unlinked from (`old`) a contact */
  card?: {
    old: CardTimelineMergedCard | null;
    new: CardTimelineMergedCard | null;
  };
  first_name?: CardTimelineDescriptionChangeXData;
  last_name?: CardTimelineDescriptionChangeXData;
  middle_name?: CardTimelineDescriptionChangeXData;
  phones?: CardTimelinePhonesChangeXData;
  emails?: CardTimelineEmailsChangeXData;
  addresses?: CardTimelineAddressesChangeXData;
}

/** `name` is missing when the company could not be found while the event was written */
export interface CardTimelineLinkedCompany {
  id: string;
  name?: string;
}

export interface CardTimelineCompanyUpdateXData {
  /** Company of a card before and after a link change: a replaced company is one event with both sides */
  company?: {
    old: CardTimelineLinkedCompany | null;
    new: CardTimelineLinkedCompany | null;
  };
  /** Card linked to (`new`) or unlinked from (`old`) a company */
  card?: {
    old: CardTimelineMergedCard | null;
    new: CardTimelineMergedCard | null;
  };
  name?: CardTimelineDescriptionChangeXData;
  phones?: CardTimelinePhonesChangeXData;
  emails?: CardTimelineEmailsChangeXData;
  addresses?: CardTimelineAddressesChangeXData;
}

export interface CardTimelineEventBase<T extends CardTimelineEventType, XData> {
  id: string;
  card_id?: string;
  entity_id?: string;
  entity_type?: string;
  event_type: T;
  action: CardTimelineEventAction;
  sub_type?: CardTimelineEventSubType | null;
  created_at: string;
  created_by?: string | null;
  created_by_user?: CardTimelineAuthor | null;
  /** What the event is about, in a timeline of several entities */
  entity?: TimelineEntityInfo | null;
  x_data: XData;
}

export type CardTimelineReminderEvent = CardTimelineEventBase<CardTimelineEventType.REMINDER, CardReminderXData>;

export type CardTimelineCommentEvent = CardTimelineEventBase<CardTimelineEventType.COMMENT, CardCommentXData>;

export type CardTimelineCallEvent = CardTimelineEventBase<CardTimelineEventType.CALL, Call>;

export type CardTimelineOtherEvent = CardTimelineEventBase<
  Exclude<CardTimelineEventType, CardTimelineEventType.REMINDER | CardTimelineEventType.COMMENT | CardTimelineEventType.CALL>,
  CardTimelineCardXData | Record<string, unknown>
>;

export type CardTimelineEvent =
  | CardTimelineReminderEvent
  | CardTimelineCommentEvent
  | CardTimelineCallEvent
  | CardTimelineOtherEvent;

export type CallType = 'in' | 'out';

export type CallDisposition = 'PROPER' | 'NO_ANSWER' | 'BUSY' | 'FAILED' | 'CANCEL' | string;

export interface CallTranscriptUtterance {
  start: number;
  end: number;
  text: string;
  speaker: string;
}

export interface CallXData {
  dst?: string;
  E164?: string;
  cdr_id?: string;
  billsec?: string;
  landing?: string;
  staffid?: number;
  calldate?: string;
  dni_type?: string;
  duration?: string;
  uniqueid?: string;
  utm_term?: string;
  call_card?: string;
  call_type?: CallType;
  client_id?: string;
  hold_time?: number;
  last_page?: string;
  pool_name?: string;
  recording?: string;
  refferrer?: string;
  remote_ip?: string;
  userfield?: string;
  department?: string;
  project_id?: number;
  user_agent?: string;
  utm_medium?: string;
  utm_source?: string;
  disposition?: CallDisposition;
  duration_ms?: number;
  proper_flag?: string;
  unique_call?: string;
  utm_content?: string;
  GOOGLE_UA_ID?: string;
  call_counter?: string;
  employee_fio?: string;
  exten_number?: string;
  first_proper?: string;
  utm_campaign?: string;
  has_recording?: string;
  recording_wav?: string;
  repeated_flag?: string;
  responsibleid?: string;
  unique_proper?: string;
  connected_with?: string;
  employee_email?: string;
  roistatvisitid?: string;
  'duration-billsec'?: number;
  extension_number?: string;
  real_waiting_time?: number;
  call_forwarding_scheme?: string;
  calldate_timestamp_micros?: string;
  [key: string]: unknown;
}

export interface Call {
  id: string;
  active: boolean;
  created_by: string | null;
  created_by_type: string | null;
  created_at: string;
  updated_by: string | null;
  updated_by_type: string | null;
  updated_at: string | null;
  deleted_by: string | null;
  deleted_by_type: string | null;
  deleted_at: string | null;
  type: string;
  company_id: string;
  channel_id: string;
  external_id: string;
  phone: string;
  call_type: CallType;
  disposition: CallDisposition;
  duration_ms: number;
  recording_url: string | null;
  x_data: CallXData;
  summary: string;
  transcription: string;
  transcript_utterances: CallTranscriptUtterance[];
}

// #region contacts

export interface BoardContactPhone {
  id: string;
  contact_id: string;
  number_e164: string;
  extension: string | null;
  type: string;
  is_primary: boolean;
  active: boolean;
  created_at: string;
}

export interface BoardContactEmail {
  id: string;
  contact_id: string;
  email: string;
  type: string;
  is_primary: boolean;
  active: boolean;
  created_at: string;
}

/** Active card the contact is linked to */
export interface BoardContactCard {
  id: string;
  name: string;
  funnel_id: string;
  column_id: string;
}

export interface BoardContact {
  id: string;
  company_id: string;
  first_name: string;
  last_name: string | null;
  middle_name: string | null;
  phones: BoardContactPhone[];
  emails: BoardContactEmail[];
  addresses?: IAddress[];
  cards: BoardContactCard[];
  active: boolean;
  created_by: string | null;
  created_at: string;
  updated_by: string | null;
  updated_at: string | null;
}

export interface BoardContactsPage {
  items: BoardContact[];
  total: number;
  size: number;
  page: number;
}

export interface GetContactsParams {
  search?: string;
  page?: number;
  take?: number;
  filters?: BoardFilterCondition[];
  filter_mode?: BoardFilterMode;
}

export interface CreateContactPayload {
  first_name: string;
  last_name?: string | null;
  middle_name?: string | null;
  /** E.164 with or without the leading `+` */
  phones?: string[];
  emails?: string[];
  addresses?: AddressInput[];
  card_ids?: string[];
}

export type UpdateContactPayload = Partial<Omit<CreateContactPayload, 'card_ids'>>;

export type NewCardContact = Omit<CreateContactPayload, 'card_ids'>;

export type CardContactInput = { id: string } | NewCardContact;

// #endregion

// #region companies

export interface BoardCompanyPhone {
  id: string;
  company_v2_id: string;
  number_e164: string;
  extension: string | null;
  type: string;
  is_primary: boolean;
  active: boolean;
  created_at: string;
}

export interface BoardCompanyEmail {
  id: string;
  company_v2_id: string;
  email: string;
  type: string;
  is_primary: boolean;
  active: boolean;
  created_at: string;
}

/** Active card the company is linked to */
export type BoardCompanyCard = BoardContactCard;

/** Client company (legal entity) of the CRM, `company_v2` on the backend; `company_id` is the tenant */
export interface BoardCompany {
  id: string;
  company_id: string;
  name: string;
  phones: BoardCompanyPhone[];
  emails: BoardCompanyEmail[];
  addresses: IAddress[];
  cards: BoardCompanyCard[];
  active: boolean;
  created_by: string | null;
  created_at: string;
  updated_by: string | null;
  updated_at: string | null;
}

export interface BoardCompaniesPage {
  items: BoardCompany[];
  total: number;
  size: number;
  page: number;
}

export interface GetCompaniesParams {
  filters?: BoardFilterCondition[];
  filter_mode?: BoardFilterMode;
  page?: number;
  take?: number;
}

export interface CreateCompanyPayload {
  name: string;
  /** E.164 with or without the leading `+` */
  phones?: string[];
  emails?: string[];
  addresses?: AddressInput[];
  /** A card that has another company gets this one instead */
  card_ids?: string[];
}

export type UpdateCompanyPayload = Partial<Omit<CreateCompanyPayload, 'card_ids'>>;

export type NewCardCompany = Omit<CreateCompanyPayload, 'card_ids'>;

export type CardCompanyInput = { id: string } | NewCardCompany;

// #endregion
